/**
 * Product Carousel Component
 *
 * Club PDP gallery. Extends the base carousel with CDP (Club Configurator) updates
 * and video playback. Keep these constraints in mind:
 *
 * - Head/club updates REPLACE all main-gallery images (not shaft/grip/headcover)
 *   and KEEP page-product videos and 3D models in their original indexes. They live
 *   on the parent (Club Family) product, not the selected head SKU, so the model
 *   only loads once no matter how many heads are viewed.
 * - Do not identify image slides by Shopify media/image ID. CDP fetches a different
 *   product, so those IDs never match and the head images get appended as duplicates.
 * - Do not sort same-category slides by data-slide-id. Media IDs are not media order.
 * - Incoming HTML is image-only; skip video/model slides from the fetched product.
 * - Click-to-play only. Leaving a slide pauses its video; only one plays at a time.
 */
import { CarouselComponent } from './carousel.js';

export class ProductCarouselComponent extends CarouselComponent {
  constructor() {
    super();
    this.unsubscribeCdpUpdate = null;
  }

  connectedCallback() {
    // Call parent's connectedCallback first
    super.connectedCallback();

    this.setupMediaPauseObserver();

    // Subscribe to CDP component update events
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeCdpUpdate = subscribe(PUB_SUB_EVENTS.cdpComponentUpdate, (data) => {
        this.handleCdpComponentUpdate(data);
      });
    }
  }

  disconnectedCallback() {
    if (this.mediaObserver) {
      this.mediaObserver.disconnect();
      this.mediaObserver = null;
    }

    // Unsubscribe from events
    if (this.unsubscribeCdpUpdate) {
      this.unsubscribeCdpUpdate();
      this.unsubscribeCdpUpdate = null;
    }
  }

  /**
   * Pause videos on slides that are no longer in view.
   * Complements pauseAllMedia() so only one gallery video plays at a time.
   */
  setupMediaPauseObserver() {
    if (this.mediaObserver) {
      this.mediaObserver.disconnect();
    }

    if (!this.track) return;

    this.mediaObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) return;
          this.pauseSlideMedia(entry.target);
        });
      },
      { root: this.track, threshold: 0.6 },
    );

    this.querySelectorAll('[data-slide-id]').forEach((slide) => {
      this.mediaObserver.observe(slide);
    });
  }

  pauseSlideMedia(slide) {
    slide.querySelectorAll('video').forEach((video) => video.pause());
    slide.querySelectorAll('product-model').forEach((model) => model.modelViewerUI?.pause());
    slide.querySelectorAll('.js-youtube').forEach((video) => {
      video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    });
    slide.querySelectorAll('.js-vimeo').forEach((video) => {
      video.contentWindow.postMessage('{"method":"pause"}', '*');
    });
  }

  /**
   * Scroll to a specific slide by its unique ID
   * @param {string} id - The unique ID of the slide to scroll to
   */
  scrollToSlide(id) {
    const targetSlide = this.findSlideById(id);
    if (!targetSlide) {
      console.warn(`Slide with ID "${id}" not found`);
      return;
    }

    const scrollPosition = targetSlide.offsetLeft;
    const behavior = this.prefersReducedMotion ? 'auto' : 'smooth';

    this.track.scrollTo({ left: scrollPosition, behavior });

    // Move focus to the target slide after scrolling
    setTimeout(() => {
      targetSlide.focus();
    }, this.prefersReducedMotion ? 0 : 300);
  }

  /**
   * Add a new item to the carousel
   * @param {string} content - HTML content to insert into the new slide
   * @param {string} id - Unique ID for the slide
   */
  addItem(content, id) {
    if (!id) {
      console.error('ID is required for adding items');
      return;
    }

    // Check if ID already exists
    if (this.findSlideById(id)) {
      console.warn(`Slide with ID "${id}" already exists`);
      return;
    }

    // Create new slide wrapper
    const newSlide = document.createElement('div');
    newSlide.className = 'carousel__slide';
    newSlide.setAttribute('data-slide-id', id);
    newSlide.innerHTML = content;

    // Append to track
    this.track.appendChild(newSlide);

    // Update button states after adding new slide
    this.handleDisabledBtns();

    // Scroll to the newly added slide
    this.scrollToSlide(id);
  }

  /**
   * Update the content of an existing slide
   * @param {string} id - Unique ID of the slide to update
   * @param {string} content - New HTML content for the slide
   */
  updateSlide(id, content) {
    const targetSlide = this.findSlideById(id);
    if (!targetSlide) {
      console.warn(`Slide with ID "${id}" not found`);
      return;
    }

    targetSlide.innerHTML = content;
  }

  /**
   * Get all slide IDs currently in the carousel
   * @returns {string[]} Array of slide IDs
   */
  getSlideIds() {
    const slides = this.querySelectorAll('[data-slide-id]');
    return Array.from(slides).map(slide => slide.getAttribute('data-slide-id'));
  }

  /**
   * Get the ID of the currently visible slide (approximation)
   * @returns {string|null} ID of the slide closest to the left edge
   */
  getCurrentSlideId() {
    const slides = this.querySelectorAll('[data-slide-id]');
    const trackLeft = this.track.scrollLeft;

    for (let slide of slides) {
      const slideLeft = slide.offsetLeft;
      const slideRight = slideLeft + slide.offsetWidth;

      // If slide is at least partially visible
      if (slideLeft <= trackLeft + this.track.offsetWidth && slideRight > trackLeft) {
        return slide.getAttribute('data-slide-id');
      }
    }

    return null;
  }

  /**
   * Remove a slide by ID
   * @param {string} id - Unique ID of the slide to remove
   */
  removeSlide(id) {
    const targetSlide = this.findSlideById(id);
    if (!targetSlide) {
      console.warn(`Slide with ID "${id}" not found`);
      return;
    }

    targetSlide.remove();
    this.handleDisabledBtns();
  }

  /**
   * Club Configurator published a new head, shaft, grip, or headcover.
   * Fetches `/products/{handle}?view=gallery-slides` and swaps images in place.
   */
  async handleCdpComponentUpdate(data) {
    if (!data?.product?.handle) {
      return;
    }

    const { handle, category } = data.product;
    const categoryName = category?.name || null;

    try {
      const slidesHtml = await this.fetchGallerySlides(handle);
      if (slidesHtml) {
        await this.updateSlidesFromCategory(categoryName, slidesHtml);
      }
    } catch (error) {
      console.error('Error handling CDP component update:', error);
    }
  }

  /**
   * Fetch gallery slides HTML from the server
   * @param {string} productHandle - Product handle to fetch slides for
   * @returns {Promise<string>} HTML string of slides
   */
  async fetchGallerySlides(productHandle) {
    if (!productHandle) {
      return '';
    }

    try {
      const response = await fetch(`/products/${productHandle}?view=gallery-slides`);
      if (!response.ok) {
        throw new Error(`Failed to fetch gallery slides: ${response.status}`);
      }
      return await response.text();
    } catch (error) {
      console.error('Error fetching gallery slides:', error);
      return '';
    }
  }

  /**
   * Get category order for sorting
   * @param {string} categoryName - Category name
   * @returns {number} Order value (lower = earlier)
   */
  getCategoryOrder(categoryName) {
    if (!categoryName) return 999;

    const orderMap = {
      'Golf Clubs': 1,
      'Golf Club Shafts': 2,
      'Golf Club Grips': 3,
      'Golf Club Headcovers': 4
    };

    return orderMap[categoryName] || 999;
  }

  isVideoSlide(slide) {
    const mediaType = slide?.getAttribute('data-media-type');
    return mediaType === 'video' || mediaType === 'external_video';
  }

  // Page-product media that survives head changes: videos and 3D models.
  isPersistentSlide(slide) {
    return this.isVideoSlide(slide) || slide?.getAttribute('data-media-type') === 'model';
  }

  // Shaft / grip / headcover: one image each. Everything else is the main club gallery.
  isAccessoryCategory(categoryName) {
    return ['Golf Club Shafts', 'Golf Club Grips', 'Golf Club Headcovers'].includes(categoryName);
  }

  normalizeThumbnailUrl(url) {
    if (!url) return '';
    return url.split('?')[0];
  }

  findSlideById(slideId) {
    if (!slideId) return null;
    const escapedId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(slideId) : slideId;
    try {
      return this.querySelector(`[data-slide-id="${escapedId}"]`);
    } catch (error) {
      return Array.from(this.querySelectorAll('[data-slide-id]')).find(
        (slide) => slide.getAttribute('data-slide-id') === slideId
      ) || null;
    }
  }

  // Match by slide ID or thumbnail path. Media IDs differ across the page product and head SKU.
  slideAlreadyExists(slide) {
    const slideId = slide.getAttribute('data-slide-id');
    if (this.findSlideById(slideId)) return true;

    const thumbnailUrl = this.normalizeThumbnailUrl(slide.getAttribute('data-thumbnail-url'));
    if (!thumbnailUrl) return false;

    return Array.from(this.track.querySelectorAll('[data-thumbnail-url]')).some((existingSlide) => {
      if (this.isPersistentSlide(existingSlide)) return false;
      return this.normalizeThumbnailUrl(existingSlide.getAttribute('data-thumbnail-url')) === thumbnailUrl;
    });
  }

  appendImportedSlide(slide) {
    if (this.slideAlreadyExists(slide)) {
      return;
    }

    this.track.appendChild(document.importNode(slide, true));
  }

  removeCategoryImageSlides(categoryName) {
    const existingSlides = Array.from(this.track.querySelectorAll('[data-component-type]'));
    existingSlides.forEach((existingSlide) => {
      const existingCategory = existingSlide.getAttribute('data-component-type');
      if (existingCategory !== categoryName) return;
      if (this.isPersistentSlide(existingSlide)) return;
      existingSlide.remove();
    });
  }

  // Drop every non-video, non-accessory image. Do not key off category name:
  // GraphQL `category.name` can disagree with Liquid `product.category.name`.
  removeMainGalleryImageSlides() {
    Array.from(this.track.querySelectorAll('[data-slide-id]')).forEach((existingSlide) => {
      if (this.isPersistentSlide(existingSlide)) return;
      if (this.isAccessoryCategory(existingSlide.getAttribute('data-component-type'))) return;
      existingSlide.remove();
    });
  }

  /**
   * Accessories: replace that category's single image.
   * Head/club: replace all main-gallery images, restore videos at their old indexes.
   */
  async updateSlidesFromCategory(categoryName, slidesHtml) {
    if (!slidesHtml || !this.track) {
      return;
    }

    // Store current active slide before updates
    const currentSlideId = this.getCurrentSlideId();
    const currentScrollPosition = this.track.scrollLeft;

    // Parse HTML to extract slide elements
    const parser = new DOMParser();
    const doc = parser.parseFromString(slidesHtml, 'text/html');
    const newSlides = Array.from(doc.querySelectorAll('.carousel__slide[data-slide-id]'));
    const imageSlides = newSlides.filter((slide) => !this.isPersistentSlide(slide));

    if (imageSlides.length === 0) {
      return;
    }

    // Prefer the category from the fetched markup; GraphQL names can differ from Liquid.
    const incomingCategory = imageSlides[0]?.getAttribute('data-component-type') || categoryName;
    const shouldAddSingleSlide = this.isAccessoryCategory(incomingCategory);
    const slidesToProcess = shouldAddSingleSlide ? [imageSlides[0]] : imageSlides;

    if (shouldAddSingleSlide) {
      this.removeCategoryImageSlides(incomingCategory);
      this.appendImportedSlide(slidesToProcess[0]);
    } else {
      // Snapshot video/model positions before images are removed, then splice them back.
      const originalSlides = Array.from(this.track.querySelectorAll('[data-slide-id]'));
      const preservedVideos = originalSlides.filter((slide) => {
        if (!this.isPersistentSlide(slide)) return false;
        return !this.isAccessoryCategory(slide.getAttribute('data-component-type'));
      });
      const videoIndexes = preservedVideos.map((slide) => originalSlides.indexOf(slide));

      this.removeMainGalleryImageSlides();
      slidesToProcess.forEach((slide) => this.appendImportedSlide(slide));

      const galleryImages = Array.from(this.track.querySelectorAll('[data-slide-id]')).filter((slide) => {
        if (this.isPersistentSlide(slide)) return false;
        return !this.isAccessoryCategory(slide.getAttribute('data-component-type'));
      });
      const merged = [...galleryImages];
      videoIndexes.forEach((originalIndex, index) => {
        const video = preservedVideos[index];
        if (!video) return;
        const insertAt = Math.min(originalIndex, merged.length);
        merged.splice(insertAt, 0, video);
      });
      merged.forEach((slide) => this.track.appendChild(slide));
    }

    // Reorder slides to maintain correct order
    this.reorderSlides();

    // Restore active slide position
    this.restoreActiveSlidePosition(currentSlideId, currentScrollPosition);

    // Update button states
    this.handleDisabledBtns();

    // Pause observers need to watch newly added slides
    this.setupMediaPauseObserver();

    // Update thumbnails if they exist
    this.updateThumbnails();
  }

  /**
   * Category order only (clubs, then shaft, grip, headcover).
   * Same-category order must stay stable — do not localeCompare slide IDs.
   */
  reorderSlides() {
    if (!this.track) return;

    const slides = Array.from(this.track.querySelectorAll('[data-slide-id]'));

    slides.sort((a, b) => {
      const categoryA = a.getAttribute('data-component-type') || '';
      const categoryB = b.getAttribute('data-component-type') || '';
      return this.getCategoryOrder(categoryA) - this.getCategoryOrder(categoryB);
    });

    // Re-insert slides in sorted order
    slides.forEach((slide) => {
      this.track.appendChild(slide);
    });
  }

  /**
   * Restore active slide position after updates
   * @param {string} previousSlideId - ID of previously active slide
   * @param {number} previousScrollPosition - Previous scroll position
   */
  restoreActiveSlidePosition(previousSlideId, previousScrollPosition) {
    if (!this.track) return;

    // Try to find the slide with the same ID
    if (previousSlideId) {
      const targetSlide = this.findSlideById(previousSlideId);
      if (targetSlide) {
        const scrollPosition = targetSlide.offsetLeft;
        this.track.scrollTo({ left: scrollPosition, behavior: 'auto' });
        return;
      }
    }

    // If slide not found, try to restore scroll position
    // Find the slide closest to the previous scroll position
    const slides = Array.from(this.track.querySelectorAll('[data-slide-id]'));
    if (slides.length === 0) return;

    let closestSlide = slides[0];
    let closestDistance = Math.abs(slides[0].offsetLeft - previousScrollPosition);

    slides.forEach((slide) => {
      const distance = Math.abs(slide.offsetLeft - previousScrollPosition);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestSlide = slide;
      }
    });

    if (closestSlide) {
      const scrollPosition = closestSlide.offsetLeft;
      this.track.scrollTo({ left: scrollPosition, behavior: 'auto' });
    }
  }

  /**
   * Update thumbnails when slides change
   */
  updateThumbnails() {
    const carouselId = this.getAttribute('data-carousel-id');
    if (!carouselId) return;

    const thumbsComponent = document.querySelector(`product-carousel-thumbs[data-carousel-id="${carouselId}"]`);
    if (typeof thumbsComponent?.syncThumbnailsWithSlides !== 'function') return;

    thumbsComponent.syncThumbnailsWithSlides();
  }
}

// Auto-register the component if not already registered
if (!customElements.get('product-carousel')) {
  customElements.define('product-carousel', ProductCarouselComponent);
}
